import { ApolloClient, useApolloClient } from '@apollo/client'
import React, { HTMLAttributes, useState } from 'react'
import { paginatedFetchQuery } from '../../utils/FetchAll'
import {
  ButtonGroup,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from '@mui/material'
import { ExportQuery, exportToXlsx, XlsxColumn } from './ExportService'
import { Icon, Spinner, Button } from '@nike/eds'

export interface ExportButtonProps extends HTMLAttributes<HTMLInputElement> {
  exportOptions: ExportOption[]
  extraData?: any
  disabled?: boolean
}

export interface ExportOption {
  label: string
  xlsxColumns: XlsxColumn[]
  exportQuery: ExportQuery
  format?: (value: any) => any
  fileName?: string
  pageSize?: number
}

export function ExportButtonWithOptions({
  exportOptions,
  extraData,
  disabled = false,
}: ExportButtonProps) {
  const client = useApolloClient()
  const [isDownloading, setIsDownloading] = useState(false)
  const anchorRef = React.useRef<HTMLDivElement>(null)
  const [popperOpen, setPopperOpen] = useState<boolean>(false)
  const isDisabled = isDownloading || disabled

  function exportXlsx(
    client: ApolloClient<object>,
    xlsxColumns: XlsxColumn[],
    exportQuery: ExportQuery,
    format: (value: any) => any,
    fileName = 'export.xlsx',
    pageSize: number
  ) {
    setIsDownloading(true)
    setPopperOpen(false)
    paginatedFetchQuery(client, exportQuery, pageSize)
      .then((data) => {
        data = format ? format(data) : data
        exportToXlsx(data, xlsxColumns, fileName, extraData)
        setIsDownloading(false)
      })
      .catch((error) => {
        console.error(error)
        alert(error)
        setIsDownloading(false)
      })
  }

  return (
    <div className="flex-row content-end" style={{ zIndex: 10 }}>
      <ButtonGroup variant="outlined" ref={anchorRef}>
        <Button
          className={`eds-button eds-button--primary eds-button--medium ${
            isDisabled && 'eds-button--disabled'
          }`}
          style={{
            padding: 'var(--eds-space-8) var(--eds-space-8) var(--eds-space-8) var(--eds-space-8)',
          }}
          disabled={isDisabled}
          onClick={() => {
            setPopperOpen(true)
          }}
          beforeSlot={<Icon name="Download" />}
        >
          Export{isDownloading && 'ing'} Results{' '}
          {isDownloading ? <Spinner color="white" /> : <Icon name="CaretDown" />}
        </Button>
      </ButtonGroup>
      <Popper
        open={popperOpen}
        anchorEl={anchorRef.current}
        transition
        disablePortal
        placement={'bottom-end'}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper elevation={12}>
              <ClickAwayListener onClickAway={() => setPopperOpen(false)}>
                <MenuList id="split-button-menu" autoFocusItem>
                  {exportOptions.map((option) => {
                    return (
                      <MenuItem
                        key={`export-option-${option.fileName}`}
                        className="eds-type--subtitle-1"
                        onClick={() => {
                          exportXlsx(
                            client,
                            option.xlsxColumns,
                            option.exportQuery,
                            option.format,
                            option.fileName,
                            option.pageSize
                          )
                        }}
                      >
                        {option.label}
                      </MenuItem>
                    )
                  })}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  )
}
